import { create } from 'zustand';
import type { GeneratedApp, AppType, AppGenerationRequest } from '@/types';
import { appService } from '@/services/apps';
import { resolveWorkspaceSessionId } from '@/services/workspaceSession';
import { wsService } from '@/services/websocket.service';

interface AppState {
  apps: GeneratedApp[];
  currentApp: GeneratedApp | null;
  isLoading: boolean;
  error: string | null;
  generationProgress: number;

  // Actions
  fetchApps: (sessionId?: string) => Promise<void>;
  fetchApp: (appId: string) => Promise<void>;
  createApp: (request: AppGenerationRequest) => Promise<GeneratedApp>;
  updateApp: (appId: string, data: Partial<GeneratedApp>) => Promise<void>;
  deleteApp: (appId: string) => Promise<void>;
  rateApp: (appId: string, rating: number) => Promise<void>;
  toggleBookmark: (appId: string) => Promise<void>;
  duplicateApp: (appId: string) => Promise<GeneratedApp>;
  regenerateApp: (appId: string, modifications?: string) => Promise<string>;
  setGenerationProgress: (progress: number) => void;
  clearError: () => void;
  initGenerationListeners: () => () => void;
}

export const useAppStore = create<AppState>((set) => ({
  apps: [],
  currentApp: null,
  isLoading: false,
  error: null,
  generationProgress: 0,

  fetchApps: async (sessionId?: string) => {
    set({ isLoading: true, error: null });
    try {
      let apps = await appService.getApps(sessionId);
      // Recover from stale client-generated session ids saved before this fix.
      if (apps.filter((a) => !a.is_shared).length === 0) {
        const freshId = await resolveWorkspaceSessionId(true);
        if (freshId && freshId !== sessionId) {
          apps = await appService.getApps(freshId);
        }
      }
      set({ apps, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch apps',
        isLoading: false,
      });
    }
  },

  fetchApp: async (appId: string) => {
    set({ isLoading: true, error: null });
    try {
      const app = await appService.getApp(appId);
      set((state) => ({
        currentApp: app,
        isLoading: false,
        apps: state.apps.some((a) => a.id === app.id)
          ? state.apps.map((a) => (a.id === app.id ? app : a))
          : [app, ...state.apps],
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch app',
        isLoading: false,
        currentApp: null,
      });
    }
  },

  createApp: async (request: AppGenerationRequest) => {
    set({ isLoading: true, error: null, generationProgress: 0 });
    try {
      const app = await appService.createApp(request);
      set(state => ({
        apps: [app, ...state.apps],
        currentApp: app,
        isLoading: false,
        generationProgress: 100,
      }));
      return app;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create app',
        isLoading: false,
      });
      throw error;
    }
  },

  updateApp: async (appId: string, data: Partial<GeneratedApp>) => {
    try {
      const updatedApp = await appService.updateApp(appId, data);
      set(state => ({
        apps: state.apps.map(app => app.id === appId ? updatedApp : app),
        currentApp: state.currentApp?.id === appId ? updatedApp : state.currentApp,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update app' });
      throw error;
    }
  },

  deleteApp: async (appId: string) => {
    try {
      await appService.deleteApp(appId);
      set(state => ({
        apps: state.apps.filter(app => app.id !== appId),
        currentApp: state.currentApp?.id === appId ? null : state.currentApp,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete app' });
      throw error;
    }
  },

  rateApp: async (appId: string, rating: number) => {
    try {
      const updatedApp = await appService.rateApp(appId, rating);
      set(state => ({
        apps: state.apps.map(app => app.id === appId ? updatedApp : app),
        currentApp: state.currentApp?.id === appId ? updatedApp : state.currentApp,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to rate app' });
      throw error;
    }
  },

  toggleBookmark: async (appId: string) => {
    try {
      const updatedApp = await appService.toggleBookmark(appId);
      set(state => ({
        apps: state.apps.map(app => app.id === appId ? updatedApp : app),
        currentApp: state.currentApp?.id === appId ? updatedApp : state.currentApp,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to toggle bookmark' });
      throw error;
    }
  },

  duplicateApp: async (appId: string) => {
    set({ isLoading: true, error: null });
    try {
      const newApp = await appService.duplicateApp(appId);
      set(state => ({
        apps: [newApp, ...state.apps],
        isLoading: false,
      }));
      return newApp;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to duplicate app',
        isLoading: false,
      });
      throw error;
    }
  },

  regenerateApp: async (appId: string, modifications?: string): Promise<string> => {
    set({ isLoading: true, error: null, generationProgress: 0 });
    try {
      const updatedApp = await appService.regenerateApp(appId, modifications);
      set(state => ({
        apps: [...state.apps.filter(a => a.id !== appId), updatedApp],
        currentApp: updatedApp,
        isLoading: false,
        generationProgress: 100,
      }));
      return updatedApp.id;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to regenerate app',
        isLoading: false,
      });
      throw error;
    }
  },

  setGenerationProgress: (progress: number) => set({ generationProgress: progress }),

  clearError: () => set({ error: null }),

  initGenerationListeners: () => {
    const handleProgress = (data: { percent: number }) => {
      set({ generationProgress: data.percent });
    };
    const handleCompleted = () => {
      set({ generationProgress: 100, isLoading: false });
    };
    const handleFailed = () => {
      set({ generationProgress: 0, isLoading: false });
    };

    wsService.on('generation_progress', handleProgress);
    wsService.on('generation_completed', handleCompleted);
    wsService.on('generation_failed', handleFailed);

    return () => {
      wsService.off('generation_progress', handleProgress);
      wsService.off('generation_completed', handleCompleted);
      wsService.off('generation_failed', handleFailed);
    };
  },
}));

// Selectors
export const useApps = () => useAppStore(state => state.apps);
export const useCurrentApp = () => useAppStore(state => state.currentApp);
export const useGenerationProgress = () => useAppStore(state => state.generationProgress);